import React from 'react';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import { dietQualityColors, getDietQualityColor } from '../../lib/themes/generalTheme';
import { getNutrientProperties } from '../NutrientsBox/utils/nutrientCalculations';
import { NUTRIENT_KEYS } from '../../lib/constants/constants';

const NutrientRangeSlider = ({
  currentValue, // Current nutrient value
  goalValue, // Goal value (if applicable)
  name, // Nutrient name
  dv = [null, null], // Daily value range [min, max]
  dailyCalories = 2000, // Daily calorie intake for adjusting DVs
  showIndicator = true, // Whether to show the indicator
}) => {
  const SODIUM = NUTRIENT_KEYS.nutrients.SODIUM;

  // Special case for sodium - invert behavior
  const isSodium = name === SODIUM;

  // Get nutrient properties from centralized utility
  const { isLimitNutrient, needsDvAdjustment } = getNutrientProperties(name);

  // Process the DV array to get min and max values
  const [minDv, maxDv] = dv;
  const hasMin = minDv !== null;
  const hasMax = maxDv !== null;

  // Adjust DV limits by calorie proportions if needed
  let adjustedMinDv = minDv;
  let adjustedMaxDv = maxDv;

  if (dailyCalories !== null && needsDvAdjustment) {
    if (hasMin) {
      adjustedMinDv = minDv * (dailyCalories / 2000);
    }
    if (hasMax) {
      adjustedMaxDv = maxDv * (dailyCalories / 2000);
    }
  }

  // Update limit nutrient status based on max DV
  const finalIsLimitNutrient = isSodium ? true : isLimitNutrient || hasMax;

  // Calculate the maximum value for the scale
  const calculatedMax =
    Math.max(currentValue * 1.2, goalValue ? goalValue * 1.2 : 0, adjustedMinDv || 0, adjustedMaxDv || 0) || 100;

  // Calculate position percentage for the current value indicator
  // For sodium, invert the calculation so lower values are on the left (green side)
  let currentPosition = (currentValue / calculatedMax) * 100;

  // Calculate position percentage for the goal (though we may use fixed positions instead)
  let goalPosition = goalValue ? (goalValue / calculatedMax) * 100 : null;

  if (isSodium) {
    // Calculate raw positions
    const rawCurrentPosition = (currentValue / calculatedMax) * 100;
    const rawGoalPosition = goalValue ? (goalValue / calculatedMax) * 100 : null;

    // Store the actual goal position for use in JSX
    goalPosition = goalValue ? Math.min(40, Math.max(10, rawGoalPosition)) : null;

    if (goalValue && currentValue === goalValue) {
      // When equal, place the current indicator at the same position as the goal
      currentPosition = goalPosition;
    } else if (goalValue && currentValue < goalValue) {
      // Find the percentage difference
      const difference = goalValue - currentValue;
      const differencePercentage = (difference / calculatedMax) * 100;

      // Make current position smaller than goal position (to the left)
      currentPosition = Math.max(5, goalPosition - differencePercentage);
    } else if (goalValue && currentValue > goalValue) {
      // Calculate how much over the goal the current value is
      const overageRatio = (currentValue - goalValue) / goalValue;

      // Start at 55% (just past center) and scale up to 95% based on overage
      // Capping at 40% range (55% to 95%) to keep visual consistency
      const scaledPosition = 55 + Math.min(40, overageRatio * 80);

      currentPosition = Math.min(95, scaledPosition);
    }
  }

  // Determine if the indicator is on the green side
  // For sodium, lower is better, so values on the left (lower percentage) are on the green side
  const isIndicatorOnGreenSide = finalIsLimitNutrient ? currentPosition <= 50 : currentPosition > 50;

  // Calculate the opacity for the green slider and goal
  const greenOpacity = isIndicatorOnGreenSide || !showIndicator ? 1 : 0.4;

  /**
   * Calculates the indicator score based on position and nutrient type
   * @param {number} position - Position percentage (0-100)
   * @param {boolean} isLimitNutrient - Whether this is a limit nutrient
   * @param {boolean} isGreen - Whether this is the green slider
   * @param {string} nutrientName - The name of the nutrient
   * @returns {number} Indicator score (1-9)
   */
  const calculateIndicatorScore = (position, isLimitNutrient, isGreen, nutrientName) => {
    // Special case for sodium - we need a gradient, not just binary colors
    if (nutrientName === SODIUM) {
      if (position <= 50) {
        // Green side (left): light green (7) to dark green (9) as we move to the left
        // 50% -> score 7, 0% -> score 9
        return 7 + Math.round(((50 - position) / 50) * 2);
      } else {
        // Red side (right): light red (5) to dark red (1) as we move to the right
        // 51% -> score 5, 100% -> score 1
        return 5 - Math.round(((position - 50) / 50) * 4);
      }
    }

    // Adjust position for each half of the slider (0-50 becomes 0-100, 51-100 becomes 0-100)
    const adjustedPosition = position <= 50 ? position * 2 : (position - 50) * 2;

    if (isLimitNutrient) {
      if (isGreen) {
        // Green bar: dark green (9) -> light green (7)
        return 9 - Math.round((adjustedPosition / 100) * 2);
      }
      // Red bar: light red (5) -> dark red (1)
      return 5 - Math.round((adjustedPosition / 100) * 4);
    }

    if (isGreen) {
      // Green bar: light green (7) -> dark green (9)
      return 7 + Math.round((adjustedPosition / 100) * 2);
    }
    // Red bar: dark red (1) -> light red (5)
    return 1 + Math.round((adjustedPosition / 100) * 4);
  };

  return (
    <div
      className="nutrient-slider-container"
      style={{
        marginBottom: '10px',
        position: 'absolute',
        width: '180px',
      }}
    >
      {/* Combined slider */}
      <div
        style={{
          display: 'flex',
          position: 'relative',
          marginTop: '12px',
        }}
      >
        {/* First gradient */}
        <div
          style={{
            flex: 1,
            height: '8px',
            width: '153px',
            borderRadius: '4px',
            opacity: finalIsLimitNutrient ? greenOpacity : 1, // Apply opacity only to green side
            background: finalIsLimitNutrient
              ? `linear-gradient(to right, ${dietQualityColors[7]}, ${dietQualityColors[9]})` // Green for limit nutrients
              : `linear-gradient(to right, ${dietQualityColors[1]}, ${dietQualityColors[5]})`, // Red for others
          }}
        ></div>

        {/* Center divider */}
        <div
          style={{
            width: '7px',
            height: '8px',
          }}
        ></div>

        {/* Second gradient */}
        <div
          style={{
            flex: 1,
            height: '8px',
            width: '153px',
            borderRadius: '4px',
            opacity: finalIsLimitNutrient ? 1 : greenOpacity, // Apply opacity only to green side
            background: finalIsLimitNutrient
              ? `linear-gradient(to right, ${dietQualityColors[5]}, ${dietQualityColors[1]})` // Red for limit nutrients
              : `linear-gradient(to right, ${dietQualityColors[7]}, ${dietQualityColors[9]})`, // Green for others
          }}
        ></div>

        {/* Goal location indicator (above the bar) - always on the appropriate side */}
        {goalValue !== null && (
          <div
            style={{
              position: 'absolute',
              left:
                currentValue === goalValue
                  ? `${currentPosition}%` // Exact alignment when values are equal
                  : isSodium
                  ? `${goalPosition}%` // Use the stored goal position for sodium
                  : finalIsLimitNutrient
                  ? // For limit nutrients, place goal closer to current position
                    `${Math.min(
                      40,
                      Math.max(
                        10,
                        // Calculate position with stronger influence from current position
                        currentPosition < 30
                          ? currentPosition + 15 // If current is on left side, place goal 15% to the right
                          : Math.max(10, currentPosition - 15), // If current is on right side, place goal on green side
                      ),
                    )}%`
                  : // For non-limit nutrients, place goal closer to current position
                    `${Math.min(
                      90,
                      Math.max(
                        60,
                        // Calculate position with stronger influence from current position
                        currentPosition > 70
                          ? currentPosition - 15 // If current is on right side, place goal 15% to the left
                          : Math.min(90, currentPosition + 15), // If current is on left side, place goal on green side
                      ),
                    )}%`,
              transform: 'translateX(-50%)',
              top: '-30px',
              zIndex: 3,
              display: 'flex',
              width: '24px',
              height: '24px',
              padding: '2px 4px 2.323px 4px',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: 1, // Always full opacity for goal location
            }}
          >
            <LocationOnRoundedIcon style={{ opacity: 0.6, color: getDietQualityColor(8), fontSize: '24px' }} />
          </div>
        )}

        {/* Current value indicator - color based on position and slider type */}
        <div
          style={{
            position: 'absolute',
            height: '20px',
            width: '8px',
            backgroundColor: getDietQualityColor(
              calculateIndicatorScore(currentPosition, finalIsLimitNutrient, isIndicatorOnGreenSide, name),
            ),
            borderRadius: '8px',
            border: '2px solid #FFF',
            left: `${currentPosition}%`,
            transform: 'translateX(-50%)',
            top: '-6px',
            zIndex: 2,
            display: showIndicator ? 'block' : 'none', // Only show if showIndicator is true
          }}
        ></div>
      </div>
    </div>
  );
};

export default NutrientRangeSlider;
